import React, { useMemo } from 'react';
import {
  Check,
  CheckCircle2,
  CircleDot,
  Layers3,
  Loader2,
  MousePointer2,
  Paintbrush,
  Pipette,
  Redo2,
  RefreshCw,
  ScanSearch,
  Trash2,
  Undo2,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { axialDisk } from './hexGridMath';

const panelCopy = {
  fr: {
    title: 'Terrain', description: 'Analyse assistée et retouches', analyze: 'Analyser la carte',
    analyzing: 'Analyse en cours…', analyzeAgain: 'Relancer l’analyse',
    analyzeHelp: 'Couleur et texture sont calculées localement ; les pixels originaux ne sont pas modifiés.',
    terrain: 'Terrain actif', sample: 'Prélever une teinte', sampleHelp: 'Cliquez un hexagone représentatif, puis recommencez pour 3 ou 4 terrains.',
    paint: 'Peindre', select: 'Sélectionner', navigate: 'Naviguer', brush: 'Taille du pinceau',
    brushHelp: 'Survolez la carte pour voir l’empreinte. Maintenez le clic gauche et déplacez la souris pour peindre en continu.',
    brushSize: 'Pinceau de {count} hexagones',
    brushSizeOne: 'Pinceau de 1 hexagone',
    oneHex: '1 hexagone', hexes: 'hexagones', applySelection: 'Appliquer à la sélection', clearSelection: 'Vider',
    samples: 'Teintes apprises', noSamples: 'Aucune teinte prélevée.', reclassify: 'Recalculer les propositions',
    analyzed: 'analysés', assigned: 'classés', ambiguous: 'à vérifier', manual: 'corrigés', selected: 'sélectionnés',
    overlay: 'Afficher les terrains', saved: 'Enregistré', saving: 'Enregistrement…', unsaved: 'Modifié',
    stale: 'La grille a changé : les anciennes zones ont été écartées. Relancez l’analyse.',
    undo: 'Annuler', redo: 'Rétablir', deleteSample: 'Supprimer cette teinte',
    guideAnalyzeTitle: '1 · Analyser la carte',
    guideAnalyze: 'Lancez l’analyse pour préparer chaque hexagone au prélèvement.',
    guideSampleTitle: '2 · Apprendre les terrains',
    guideSample: '{count} hexagones prêts. Choisissez un terrain puis utilisez la pipette sur la carte.',
    guideSecond: 'Une teinte est apprise. Ajoutez un second type de terrain pour afficher les propositions.',
    guideRefineTitle: '2 · Affiner les propositions',
    guideRefine: '{count} types appris. Ajoutez idéalement 3 ou 4 terrains représentatifs.',
    guideCorrectTitle: '3 · Corriger les ambiguïtés',
    guideCorrect: 'Les propositions sont visibles. Peignez seulement les zones douteuses.',
    guideErrorTitle: 'Analyse interrompue',
    guideError: 'Relancez l’analyse. Vos corrections manuelles sont conservées.',
    learnedTypes: 'types appris',
  },
  en: {
    title: 'Terrain', description: 'Assisted analysis and corrections', analyze: 'Analyze map',
    analyzing: 'Analyzing…', analyzeAgain: 'Run analysis again',
    analyzeHelp: 'Color and local texture are computed locally; original pixels are not changed.',
    terrain: 'Active terrain', sample: 'Sample a color', sampleHelp: 'Click a representative hex, then repeat for 3 or 4 terrains.',
    paint: 'Paint', select: 'Select', navigate: 'Navigate', brush: 'Brush size',
    brushHelp: 'Hover the map to preview the footprint. Hold the left mouse button and drag to paint continuously.',
    brushSize: '{count}-hex brush',
    brushSizeOne: '1-hex brush',
    oneHex: '1 hex', hexes: 'hexes', applySelection: 'Apply to selection', clearSelection: 'Clear',
    samples: 'Learned colors', noSamples: 'No color sampled yet.', reclassify: 'Recompute suggestions',
    analyzed: 'analyzed', assigned: 'classified', ambiguous: 'to review', manual: 'corrected', selected: 'selected',
    overlay: 'Show terrain', saved: 'Saved', saving: 'Saving…', unsaved: 'Changed',
    stale: 'The grid changed: old areas were discarded. Run analysis again.',
    undo: 'Undo', redo: 'Redo', deleteSample: 'Delete this sample',
    guideAnalyzeTitle: '1 · Analyze the map',
    guideAnalyze: 'Run the analysis to prepare every hex for sampling.',
    guideSampleTitle: '2 · Teach terrains',
    guideSample: '{count} hexes ready. Choose a terrain, then use the eyedropper on the map.',
    guideSecond: 'One color is learned. Add a second terrain type to display proposals.',
    guideRefineTitle: '2 · Refine proposals',
    guideRefine: '{count} types learned. Ideally add 3 or 4 representative terrains.',
    guideCorrectTitle: '3 · Correct ambiguities',
    guideCorrect: 'Proposals are visible. Paint only the uncertain areas.',
    guideErrorTitle: 'Analysis interrupted',
    guideError: 'Run the analysis again. Manual corrections are preserved.',
    learnedTypes: 'types learned',
  },
};

const terrainLabel = (terrain, language) => terrain?.label?.[language] || terrain?.label?.fr || terrain?.id;

const MINI_HEX_SIZE = 4.2;
const MINI_HEX_POINTS = Array.from({ length: 6 }, (_, index) => {
  const angle = (-90 + index * 60) * Math.PI / 180;
  return {
    x: MINI_HEX_SIZE * Math.cos(angle),
    y: MINI_HEX_SIZE * Math.sin(angle),
  };
});

function BrushFootprint({ radius, active }) {
  const cells = axialDisk({ q: 0, r: 0 }, radius);
  return (
    <svg viewBox="-34 -25 68 50" className="h-10 w-full" aria-hidden="true">
      {cells.map(({ q, r }) => {
        const centerX = Math.sqrt(3) * MINI_HEX_SIZE * (q + r / 2);
        const centerY = 1.5 * MINI_HEX_SIZE * r;
        const points = MINI_HEX_POINTS.map(({ x, y }) => `${centerX + x},${centerY + y}`).join(' ');
        return (
          <polygon
            key={`${q},${r}`}
            points={points}
            fill="currentColor"
            fillOpacity={active ? 0.28 : 0.08}
            stroke="currentColor"
            strokeWidth={active ? 1 : 0.7}
          />
        );
      })}
    </svg>
  );
}

export function TravelTerrainPanel({
  language = 'fr',
  terrains = [],
  samples = [],
  selectedTerrainId,
  onSelectedTerrainChange,
  interactionMode,
  onInteractionModeChange,
  brushRadius,
  onBrushRadiusChange,
  analysisStatus = 'idle',
  analysisProgress = 0,
  analyzedHexCount = 0,
  onAnalyze,
  onReclassify,
  onRemoveSample,
  stats,
  selectedCount = 0,
  onApplySelection,
  onClearSelection,
  overlayEnabled,
  onOverlayEnabledChange,
  saveStatus = 'saved',
  stale = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
}) {
  const text = panelCopy[language] || panelCopy.fr;
  const byId = useMemo(() => Object.fromEntries(terrains.map((terrain) => [terrain.id, terrain])), [terrains]);
  const status = {
    saved: { label: text.saved, icon: Check, className: 'text-emerald-600 dark:text-emerald-300' },
    saving: { label: text.saving, icon: Loader2, className: 'text-amber-600 dark:text-amber-300' },
    dirty: { label: text.unsaved, icon: CircleDot, className: 'text-muted-foreground' },
  }[saveStatus];
  const StatusIcon = status.icon;
  const analysisReady = analysisStatus === 'ready' && analyzedHexCount > 0;
  const sampledTerrainCount = useMemo(
    () => new Set(samples.map((sample) => sample.terrainId)).size,
    [samples],
  );
  const recommendedTerrainCount = Math.min(4, terrains.length);
  const guide = analysisStatus === 'error'
    ? { title: text.guideErrorTitle, description: text.guideError, tone: 'border-destructive/30 bg-destructive/10 text-destructive' }
    : !analysisReady
      ? { title: text.guideAnalyzeTitle, description: text.guideAnalyze, tone: 'border-border bg-muted/25' }
      : sampledTerrainCount === 0
        ? {
          title: text.guideSampleTitle,
          description: text.guideSample.replace('{count}', analyzedHexCount.toLocaleString(language)),
          tone: 'border-primary/30 bg-primary/10',
        }
        : sampledTerrainCount < 2
          ? { title: text.guideSampleTitle, description: text.guideSecond, tone: 'border-amber-500/30 bg-amber-500/10' }
          : sampledTerrainCount < recommendedTerrainCount
            ? {
              title: text.guideRefineTitle,
              description: text.guideRefine.replace('{count}', sampledTerrainCount.toLocaleString(language)),
              tone: 'border-amber-500/30 bg-amber-500/10',
            }
            : { title: text.guideCorrectTitle, description: text.guideCorrect, tone: 'border-emerald-500/30 bg-emerald-500/10' };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Layers3 className="h-4 w-4" />{text.title}</CardTitle>
            <CardDescription>{text.description}</CardDescription>
          </div>
          <Badge variant="outline" className={status.className}>
            <StatusIcon className={`mr-1 h-3 w-3 ${saveStatus === 'saving' ? 'animate-spin' : ''}`} />{status.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {stale && <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200">{text.stale}</p>}

        <div className={`rounded-lg border p-3 text-xs ${guide.tone}`}>
          <p className="flex items-center gap-2 font-semibold">
            {analysisReady && sampledTerrainCount >= 2 && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            {guide.title}
          </p>
          <p className="mt-1 leading-relaxed text-muted-foreground">{guide.description}</p>
        </div>

        <div className="space-y-2">
          <Button className="w-full" onClick={onAnalyze} disabled={analysisStatus === 'running'}>
            {analysisStatus === 'running' ? <Loader2 className="animate-spin" /> : <ScanSearch />}
            {analysisStatus === 'running' ? text.analyzing : analysisReady ? text.analyzeAgain : text.analyze}
          </Button>
          {analysisStatus === 'running' && <Progress value={analysisProgress * 100} />}
          <p className="text-[11px] leading-relaxed text-muted-foreground">{text.analyzeHelp}</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            [analyzedHexCount, text.analyzed],
            [stats.total, text.assigned],
            [stats.ambiguous, text.ambiguous],
            [stats.manual, text.manual],
          ].map(([value, label]) => (
            <div key={label} className="rounded-md border bg-muted/20 px-2 py-2 text-center">
              <p className="text-base font-semibold tabular-nums">{value}</p>
              <p className="text-[10px] text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="travel-terrain-overlay">{text.overlay}</Label>
          <Switch id="travel-terrain-overlay" checked={overlayEnabled} onCheckedChange={onOverlayEnabledChange} />
        </div>

        <div className="space-y-2">
          <Label>{text.terrain}</Label>
          <Select value={selectedTerrainId || ''} onValueChange={onSelectedTerrainChange}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {terrains.map((terrain) => (
                <SelectItem key={terrain.id} value={terrain.id}>
                  <span className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full border" style={{ backgroundColor: terrain.referenceColors?.[0] }} />
                    {terrainLabel(terrain, language)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
          {[
            ['navigate', MousePointer2, text.navigate],
            ['terrain_sample', Pipette, text.sample],
            ['terrain_paint', Paintbrush, text.paint],
          ].map(([mode, Icon, label]) => (
            <Button key={mode} type="button" size="sm" variant={interactionMode === mode ? 'secondary' : 'ghost'} className="h-auto min-w-0 flex-col gap-1 px-1 py-2 text-[10px]" disabled={mode === 'terrain_sample' && !analysisReady} onClick={() => onInteractionModeChange(mode)}>
              <Icon className="h-4 w-4" />{label}
            </Button>
          ))}
        </div>
        {interactionMode === 'terrain_sample' && <p className="-mt-2 text-xs text-muted-foreground">{text.sampleHelp}</p>}

        <div className="space-y-2">
          <div className="flex items-center justify-between"><Label>{text.brush}</Label><span className="text-xs text-muted-foreground">{1 + 3 * brushRadius * (brushRadius + 1)} {brushRadius === 0 ? text.oneHex.replace('1 ', '') : text.hexes}</span></div>
          <div className="grid grid-cols-4 gap-1">
            {[0, 1, 2, 3].map((radius) => {
              const count = 1 + 3 * radius * (radius + 1);
              const label = count === 1 ? text.brushSizeOne : text.brushSize.replace('{count}', count);
              return (
                <Button
                  key={radius}
                  type="button"
                  variant={brushRadius === radius ? 'secondary' : 'outline'}
                  className="h-14 min-w-0 px-1 py-1"
                  aria-label={label}
                  title={label}
                  onClick={() => {
                    onBrushRadiusChange(radius);
                    onInteractionModeChange('terrain_paint');
                  }}
                >
                  <BrushFootprint radius={radius} active={brushRadius === radius} />
                </Button>
              );
            })}
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{text.brushHelp}</p>
        </div>

        <Button variant={interactionMode === 'terrain_select' ? 'secondary' : 'outline'} className="w-full" onClick={() => onInteractionModeChange(interactionMode === 'terrain_select' ? 'navigate' : 'terrain_select')}>
          <MousePointer2 />{text.select}{selectedCount ? ` · ${selectedCount}` : ''}
        </Button>
        {selectedCount > 0 && (
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Button onClick={onApplySelection}>{text.applySelection}</Button>
            <Button variant="ghost" size="icon" onClick={onClearSelection} title={text.clearSelection}><Trash2 /></Button>
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" disabled={!canUndo} onClick={onUndo}><Undo2 />{text.undo}</Button>
          <Button variant="outline" size="sm" className="flex-1" disabled={!canRedo} onClick={onRedo}><Redo2 />{text.redo}</Button>
        </div>

        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <Label>{text.samples}</Label>
              <p className="text-[10px] text-muted-foreground">{sampledTerrainCount}/{recommendedTerrainCount} {text.learnedTypes}</p>
            </div>
            <Button variant="ghost" size="sm" disabled={sampledTerrainCount < 2 || !analysisReady} onClick={onReclassify}><RefreshCw />{text.reclassify}</Button>
          </div>
          {!samples.length ? <p className="text-xs text-muted-foreground">{text.noSamples}</p> : (
            <div className="space-y-1.5">
              {samples.map((sample) => {
                const terrain = byId[sample.terrainId];
                return (
                  <div key={sample.id} className="flex items-center gap-2 rounded-md bg-muted/35 px-2 py-1.5">
                    <span className="h-5 w-5 shrink-0 rounded border" style={{ backgroundColor: `rgb(${sample.color.r} ${sample.color.g} ${sample.color.b})` }} />
                    <span className="min-w-0 flex-1 truncate text-xs">{terrainLabel(terrain, language)}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemoveSample(sample.id)} title={text.deleteSample}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default TravelTerrainPanel;
